import React from 'react';

import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';

import { Box, type SxProps, type Theme } from '@mui/material';

import { reorderPinnedIds } from '~/pages/AskRichardUnified/lensPins';

import SortablePin from './SortablePin';

export default function PinnedOrderList<T extends { id: string; title: string }>({
  items,
  orderedIds,
  onReorder,
  children,
  sx,
}: {
  items: Array<T>;
  orderedIds: Array<string>;
  onReorder: (ids: Array<string>) => void;
  children: (item: T, handle: React.ReactNode) => React.ReactNode;
  sx?: SxProps<Theme>;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={({ active, over }) => {
        if (over && active.id !== over.id) onReorder(reorderPinnedIds(orderedIds, String(active.id), String(over.id)));
      }}
    >
      <SortableContext items={items.map(({ id }) => id)} strategy={rectSortingStrategy}>
        <Box sx={sx}>
          {items.map((item) => (
            <SortablePin key={item.id} id={item.id} label={item.title}>
              {(handle) => children(item, handle)}
            </SortablePin>
          ))}
        </Box>
      </SortableContext>
    </DndContext>
  );
}
